import { inject, Injectable } from '@angular/core';
import { catchError, combineLatest, forkJoin, mergeMap, Observable, of } from 'rxjs';
import { filter, map, switchMap, take, tap } from 'rxjs/operators';

import {
    ContractDetails,
    HuismapType,
    MijnOverzichtResource,
    Product,
    ProductGroep,
    RekeningsoortType,
    ZelfRegelenDetails
} from '@ap/interface-apix-mijn-overzicht';
import { MijnLaunchDarklyService } from '@ap/mijn-kanaalconfiguratie';
import { CustomerSettingCategory, CustomerSettingName, MijnKlantinstellingenService } from '@ap/mijn-klantinstellingen';

import { ConditionsConfigs } from './config/conditions';
import { ACCOUNT_FILTER_ACTIVE } from './mijn-condities.const';
import {
    AsyncConditions,
    ConditionParameters,
    defaultAsyncConditions,
    defaultContractDetails,
    defaultZelfRegelenDetails,
    IDEAL_ENABLED,
    INSURANCE_ACTIVE,
    PRODUCT_CATALOG_ENABLED,
    RekeningSelectieDTO
} from './mijn-condities.model';

@Injectable({ providedIn: 'root' })
export class MijnConditiesService {
    private products: Product[];
    private asyncConditions: AsyncConditions;
    private zelfRegelenDetails: ZelfRegelenDetails;
    private conditionParameters: ConditionParameters;

    private readonly conditionsConfigs = ConditionsConfigs;

    private readonly launchdarklyService: MijnLaunchDarklyService = inject(MijnLaunchDarklyService);
    private readonly mijnKlantinstellingenService: MijnKlantinstellingenService = inject(MijnKlantinstellingenService);
    private readonly mijnOverzichtResource: MijnOverzichtResource = inject(MijnOverzichtResource);

    private static hasPaymentAccounts(conditionParameters: ConditionParameters): boolean {
        return (
            conditionParameters.zelfRegelenDetails.productgroepen.includes(ProductGroep.BETALEN_LENEN) &&
            conditionParameters.producten.some((product) => product.interne_betaling_toegestaan || product.externe_betaling_toegestaan)
        );
    }

    private static hasPaymentSavingsInvestmentAccounts(conditionParameters: ConditionParameters): boolean {
        return (
            conditionParameters.zelfRegelenDetails.productgroepen.includes(ProductGroep.BETALEN_LENEN) ||
            conditionParameters.zelfRegelenDetails.productgroepen.includes(ProductGroep.SPAREN) ||
            conditionParameters.zelfRegelenDetails.productgroepen.includes(ProductGroep.BELEGGEN)
        );
    }

    private static hasOnlySavingsAccounts(conditionParameters: ConditionParameters): boolean {
        return (
            conditionParameters.zelfRegelenDetails.productgroepen.length === 1 &&
            conditionParameters.zelfRegelenDetails.productgroepen.includes(ProductGroep.SPAREN)
        );
    }

    private static hasPaymentSavingsInvestmentAccountsAndIsZZP(conditionParameters: ConditionParameters): boolean {
        return (
            MijnConditiesService.hasPaymentSavingsInvestmentAccounts(conditionParameters) && MijnConditiesService.isZZP(conditionParameters)
        );
    }

    private static hasRSCForAutomaticDebit(conditionParameters: ConditionParameters): boolean {
        return conditionParameters.zelfRegelenDetails.automatische_afschrijving_toegestaan;
    }

    private static hasPaymentSavingsInvestmentAccountsOrMortgageWithBouwdepot(conditionParameters: ConditionParameters): boolean {
        return (
            (MijnConditiesService.hasPaymentSavingsInvestmentAccounts(conditionParameters) ||
                conditionParameters.producten.some((product) => MijnConditiesService.isBouwdepot(product))) &&
            !MijnConditiesService.hasOnlyBouwdepotAccounts(conditionParameters)
        );
    }

    private static hasDisplayableAccountAndNoAccountFilter(conditionParameters: ConditionParameters): boolean {
        return (
            MijnConditiesService.hasPaymentSavingsInvestmentAccountsOrMortgageWithBouwdepot(conditionParameters) &&
            !MijnConditiesService.hasAccountFilter(conditionParameters)
        );
    }

    private static hasMultiplePaymentAccountsAndNoAccountFilter(conditionParameters: ConditionParameters): boolean {
        return (
            !MijnConditiesService.hasAccountFilter(conditionParameters) &&
            conditionParameters.producten.filter(
                (product) => product.productgroep === ProductGroep.BETALEN_LENEN && product.rekeningsoort === RekeningsoortType.OVERIG
            ).length > 1
        );
    }

    private static hasOnlyBouwdepotAccounts(conditionParameters: ConditionParameters): boolean {
        for (const product of conditionParameters.producten) {
            if (!MijnConditiesService.isBouwdepot(product)) {
                return false;
            }
        }
        return true;
    }

    private static isBouwdepot(product: Product): boolean {
        return product.rekeningsoort === RekeningsoortType.BOUWDEPOT;
    }

    private static isBatchbetaling(product: Product): boolean {
        return product.rekeningsoort === RekeningsoortType.BATCHBETALING;
    }

    private static isZZP(conditionParameters: ConditionParameters): boolean {
        return conditionParameters.contractDetails.is_zzp;
    }

    private static isBusiness(conditionParameters: ConditionParameters): boolean {
        return conditionParameters.contractDetails.is_zakelijk;
    }

    private static isNotBusiness(conditionParameters: ConditionParameters): boolean {
        return !conditionParameters.contractDetails.is_zakelijk;
    }

    private static hasAccountFilter(conditionParameters: ConditionParameters): boolean {
        return conditionParameters.contractDetails.gebruik_rekening_filter;
    }

    private static hasBatchPayments(conditionParameters: ConditionParameters): boolean {
        return (
            conditionParameters.producten.some((product) => this.isBatchbetaling(product)) || !!this.hasAccountFilter(conditionParameters)
        );
    }

    private static hasPaymentSavingsAccounts(conditionParameters: ConditionParameters): boolean {
        return (
            (conditionParameters.zelfRegelenDetails.productgroepen.includes(ProductGroep.BETALEN_LENEN) ||
                conditionParameters.zelfRegelenDetails.productgroepen.includes(ProductGroep.SPAREN)) &&
            conditionParameters.producten.some((product) => product.interne_betaling_toegestaan || product.externe_betaling_toegestaan)
        );
    }

    private static hasSavingsInvestmentLoanAccounts(conditionParameters: ConditionParameters): boolean {
        return (
            conditionParameters.zelfRegelenDetails.productgroepen.includes(ProductGroep.SPAREN) ||
            conditionParameters.zelfRegelenDetails.productgroepen.includes(ProductGroep.BELEGGEN) ||
            conditionParameters.zelfRegelenDetails.productgroepen.includes(ProductGroep.BETALEN_LENEN)
        );
    }

    private static hasSavingsInvestmentLoanAccountsAndIsNotZZP(conditionParameters: ConditionParameters): boolean {
        return (
            MijnConditiesService.hasSavingsInvestmentLoanAccounts(conditionParameters) && !MijnConditiesService.isZZP(conditionParameters)
        );
    }

    private static hasMortgages(conditionParameters: ConditionParameters): boolean {
        return conditionParameters.zelfRegelenDetails.productgroepen.includes(ProductGroep.HYPOTHEKEN);
    }

    private static hasOnlyMortgages(conditionParameters: ConditionParameters): boolean {
        return (
            conditionParameters.zelfRegelenDetails.productgroepen.includes(ProductGroep.HYPOTHEKEN) &&
            !MijnConditiesService.hasPaymentSavingsInvestmentAccounts(conditionParameters)
        );
    }

    private static canConvertMortgage(conditionParameters: ConditionParameters): boolean {
        return conditionParameters.zelfRegelenDetails.mag_aflossingsvrij_hypotheek_omzetten;
    }

    private static hasConstructionDepotWithNonZeroAmount(conditionParameters: ConditionParameters): boolean {
        return conditionParameters.producten.some((product) => this.isBouwdepot(product) && product.saldo?.amount > 0);
    }

    private static hasInsurances(conditionParameters: ConditionParameters): boolean {
        return (
            conditionParameters.zelfRegelenDetails.productgroepen.includes(ProductGroep.VERZEKEREN) &&
            conditionParameters.asyncConditions.insuranceEnabled
        );
    }

    private static hasPendingPayments(conditionParameters: ConditionParameters): boolean {
        return conditionParameters.producten.some(
            (product) => !isNaN(Number(product.aantal_te_verzenden_opdrachten)) && Number(product.aantal_te_verzenden_opdrachten) > 0
        );
    }

    private static hasDigicode(conditionParameters: ConditionParameters): boolean {
        return conditionParameters.zelfRegelenDetails.heeft_digicode;
    }

    private static hasAccesstoChildAccount(conditionParameters: ConditionParameters): boolean {
        return conditionParameters.zelfRegelenDetails.heeft_toegang_tot_kind_rekeningen;
    }

    private static hasDigipas(conditionParameters: ConditionParameters): boolean {
        return conditionParameters.zelfRegelenDetails.heeft_digipas;
    }

    private static hasHuismap(conditionParameters: ConditionParameters): boolean {
        return (
            conditionParameters.zelfRegelenDetails.heeft_huismap && conditionParameters.zelfRegelenDetails.huismap_type === HuismapType.MIJN
        );
    }

    private static hasBizcuitConnection(conditionParameters: ConditionParameters): boolean {
        return conditionParameters.contractDetails.is_zakelijk && conditionParameters.zelfRegelenDetails.heeft_bizcuit_koppeling;
    }

    private static hasPegaHuismap(conditionParameters: ConditionParameters): boolean {
        return (
            conditionParameters.zelfRegelenDetails.heeft_huismap && conditionParameters.zelfRegelenDetails.huismap_type === HuismapType.PEGA
        );
    }

    private static hasIdealProfile(conditionParameters: ConditionParameters): boolean {
        return conditionParameters.asyncConditions.idealEnabled && conditionParameters.zelfRegelenDetails.mag_ideal_profiel_aanmaken;
    }

    private static hasCancelledAccounts(conditionParameters: ConditionParameters): boolean {
        return conditionParameters.zelfRegelenDetails.heeft_afbetaalde_rekening;
    }

    private static hasOnlyCancelledAccounts(conditionParameters: ConditionParameters): boolean {
        return (
            MijnConditiesService.hasCancelledAccounts(conditionParameters) &&
            !MijnConditiesService.hasPaymentSavingsInvestmentAccountsOrMortgageWithBouwdepot(conditionParameters)
        );
    }

    private static hasOrCanRequestCards(conditionParameters: ConditionParameters): boolean {
        return (
            conditionParameters.zelfRegelenDetails.heeft_betaalpas || conditionParameters.zelfRegelenDetails.kan_nieuwe_betaalpas_aanvragen
        );
    }

    private static hasOrCanRequestCardsAndIsZZP(conditionParameters: ConditionParameters): boolean {
        return MijnConditiesService.hasOrCanRequestCards(conditionParameters) && MijnConditiesService.isZZP(conditionParameters);
    }

    private static hasMortgageWithSavingAttributes(conditionParameters: ConditionParameters): boolean {
        return conditionParameters.zelfRegelenDetails.kan_hypotheek_extra_storten;
    }

    private static hasInsurancesAndIsZZP(conditionParameters: ConditionParameters): boolean {
        return MijnConditiesService.hasInsurances(conditionParameters) && MijnConditiesService.isZZP(conditionParameters);
    }

    private static isAdult(conditionParameters: ConditionParameters): boolean {
        return conditionParameters.zelfRegelenDetails.is_meerderjarig;
    }

    private static productCatalogEnabled(conditionParameters: ConditionParameters): boolean {
        return conditionParameters.asyncConditions.productCatalogEnabled;
    }

    public initializeConditionParameters(): Observable<ConditionParameters> {
        return forkJoin([this.getProducten(), this.getZelfRegelenDetails(), this.getContractDetails(), this.getAsyncConditions()]).pipe(
            map(([producten, zelfRegelenDetails, contractDetails, asyncConditions]) => {
                this.conditionParameters = {
                    producten,
                    zelfRegelenDetails,
                    contractDetails,
                    asyncConditions
                };
                return this.conditionParameters;
            }),
            catchError(() => {
                this.conditionParameters = {
                    producten: [],
                    zelfRegelenDetails: defaultZelfRegelenDetails,
                    contractDetails: defaultContractDetails,
                    asyncConditions: defaultAsyncConditions
                };
                return of(this.conditionParameters);
            })
        );
    }

    public checkContentKeyCondition(contentKey: string): boolean {
        const replaceRegex = /mijn.klant.zelfservice.|mijn.template./gi;

        const level1 = contentKey.replace(replaceRegex, '').split('.')[0];
        const level1Condition = this.conditionsConfigs.find(
            (conditionConfig) => conditionConfig.level === 1 && conditionConfig.keyPart === level1
        );

        const level2Items = contentKey.replace(replaceRegex, '').split('.');
        level2Items.pop();
        const level2 = level2Items.join('.');
        const level2Condition = this.conditionsConfigs.find(
            (conditionConfig) => conditionConfig.level === 2 && conditionConfig.keyPart === level2
        );

        if (level1Condition && !level2Condition) {
            return this.checkCondition(level1Condition.condition, level1Condition.businessCondition);
        }
        return !!(level2Condition && this.checkCondition(level2Condition.condition, level2Condition.businessCondition));
    }

    private checkCondition(condition: string, businessCondition?: string): boolean {
        if (!!this.conditionParameters) {
            if (this.conditionParameters.contractDetails.is_zakelijk && !!businessCondition) {
                return !!(
                    typeof MijnConditiesService[businessCondition] === 'function' &&
                    MijnConditiesService[businessCondition](this.conditionParameters)
                );
            }
            return !!(typeof MijnConditiesService[condition] === 'function' && MijnConditiesService[condition](this.conditionParameters));
        }
        return true;
    }

    private getProducten(): Observable<Product[]> {
        if (this.products) {
            return of(this.products);
        }

        return this.launchdarklyService.getFlagValue(ACCOUNT_FILTER_ACTIVE, false).pipe(
            mergeMap((useAccountFilter) => {
                if (useAccountFilter) {
                    return this.getContractDetails().pipe(map((result) => result.gebruik_rekening_filter ?? false));
                }
                return of(false);
            }),
            mergeMap((useFilter) => {
                if (useFilter) {
                    return (
                        this.mijnKlantinstellingenService.getCustomerSetting(
                            CustomerSettingCategory.MIJN_KLANT,
                            CustomerSettingName.MIJN_OVERZICHT_REKENING_SELECTIE,
                            false,
                            JSON.stringify({
                                rekeningen: []
                            })
                        ) as unknown as Observable<RekeningSelectieDTO>
                    ).pipe(switchMap((result) => this.mijnOverzichtResource.getProducten([], result.rekeningen ?? [])));
                }
                return this.mijnOverzichtResource.getProducten([]);
            }),
            catchError(() => {
                return of({ producten: [] });
            }),
            filter((result) => Boolean(result?.producten)),
            map((productsList) => productsList?.producten),
            tap((productsArray) => {
                this.products = productsArray;
            })
        );
    }

    private getZelfRegelenDetails(): Observable<ZelfRegelenDetails | undefined> {
        if (this.zelfRegelenDetails) {
            return of(this.zelfRegelenDetails);
        }
        return this.mijnOverzichtResource.getZelfRegelenDetails().pipe(
            catchError((err) => {
                if (err.status === 401 || err.status === 403) {
                    return of(defaultZelfRegelenDetails);
                }
                return of(undefined);
            }),
            tap((result) => {
                this.zelfRegelenDetails = result;
                if (this.zelfRegelenDetails.productgroepen == null) {
                    this.zelfRegelenDetails.productgroepen = [];
                }
            })
        );
    }

    private getContractDetails(): Observable<ContractDetails> {
        return this.mijnOverzichtResource.getContractDetails().pipe(
            catchError((err) => {
                if (err.status === 401 || err.status === 403) {
                    return of(defaultContractDetails);
                }
                return of(undefined);
            })
        );
    }

    private getAsyncConditions(): Observable<AsyncConditions> {
        if (this.asyncConditions) {
            return of(this.asyncConditions);
        }

        return combineLatest([
            this.launchdarklyService.getFlagValue(INSURANCE_ACTIVE, false).pipe(take(1)),
            this.launchdarklyService.getFlagValue(PRODUCT_CATALOG_ENABLED, false).pipe(take(1)),
            this.launchdarklyService.getFlagValue(IDEAL_ENABLED, false).pipe(take(1))
        ]).pipe(
            catchError(() => {
                return of([false, false, false]);
            }),
            map(([insuranceEnabled, productCatalogEnabled, idealEnabled]) => ({
                insuranceEnabled,
                productCatalogEnabled,
                idealEnabled
            })),
            tap((asyncConditions) => {
                this.asyncConditions = asyncConditions;
            })
        );
    }
}
